import { el, setStyle } from 'redom';

import ToggleInput from 'generics/components/toggle-input';
import SingleSelectInputBox from 'generics/components/single-select/native/input-box';
import CSSNumberValueControl from 'generics/components/css-number-value-control/native';

import CarouselItemSettings from './item-settings';

import './index.scss';

const POSITION_OPTIONS = [{
  label: 'Top',
  value: 'top'
}, {
  label: 'Bottom',
  value: 'bottom'
}];

const APPEARANCE_OPTIONS = [{
  label: 'Static',
  value: 'static'
}, {
  label: 'Hover',
  value: 'hover'
}];

const TRANSITION_OPTIONS = [{
  label: 'Smooth',
  value: 'smooth'
}, {
  label: 'Spring',
  value: 'spring'
}, {
  label: 'Linear',
  value: 'linear'
}, {
  label: 'Instant',
  value: 'instant'
}, {
  label: 'Continuous',
  value: 'continuous'
}];

function getOptionIndex(options, value) {

  for (let index = 0; index < options.length; index++) {

    if (options[index].value === value) {
      return index;
    }

  }

  return 0;

}

class CarouselSettings {

  constructor(configuration) {

    this.onChange = configuration.onChange;
    this.settings = { ...configuration.settings };

    this.itemSettings = new CarouselItemSettings({
      settings: this.settings,
      onChange: this.propagateChange.bind(this)
    });

    this.buildItemSpacingInput();
    this.buildTransitionSelect();
    this.buildShowArrowsToggle();
    this.buildShowDotsToggle();
    this.buildAutoplayToggle();
    this.buildAutoplayIntervalInput();
    this.buildLoopToggle();
    this.buildFullViewToggle();
    this.buildCaptionToggle();
    this.buildCaptionPositionSelect();
    this.buildCaptionAppearanceSelect();

    this.autoplaySubSettings = el('.carousel-settings-sub', [
      this.buildRow('Autoplay interval (seconds)', this.autoplayIntervalInput)
    ]);

    this.captionSubSettings = el('.carousel-settings-sub', [
      this.buildRow('Caption position', this.captionPositionSelect),
      this.buildRow('Caption appearance', this.captionAppearanceSelect)
    ]);

    this.el = el('.carousel-settings-main', [
      this.buildRow('Items in view', this.itemSettings.itemsPerViewInput),
      this.buildRow('Item spacing', this.itemSpacingInput),
      this.buildRow('Image fit', this.itemSettings.imageFitSelect),
      this.buildRow('Image shape', this.itemSettings.imageRatioSelect),
      this.buildRow('Transition', this.transitionSelect),
      this.buildRow('Navigation arrows', this.showArrowsToggle, '.toggle-row'),
      this.buildRow('Pagination dots', this.showDotsToggle, '.toggle-row'),
      this.buildRow('Autoplay', this.autoplayToggle, '.toggle-row'),
      this.autoplaySubSettings,
      this.buildRow('Loop', this.loopToggle, '.toggle-row'),
      this.buildRow('Full view on click', this.fullViewToggle, '.toggle-row'),
      this.buildRow('Captions', this.captionToggle, '.toggle-row'),
      this.captionSubSettings
    ]);

    this.applyAutoplayVisibility();
    this.applyCaptionVisibility();

  }

  buildRow(labelText, controlElement, cls = '') {

    return el('.carousel-settings-row' + cls, [
      el('label.carousel-settings-label.field-label', labelText),
      controlElement
    ]);

  }

  buildItemSpacingInput() {

    this.itemSpacingInput = new CSSNumberValueControl({
      value: this.settings.itemSpacing,
      onChange: this.handleItemSpacingInput.bind(this)
    });

  }

  buildTransitionSelect() {

    this.transitionSelect = new SingleSelectInputBox({
      placeholder: 'Select transition',
      options: TRANSITION_OPTIONS,
      selectedOptionIndices: [getOptionIndex(TRANSITION_OPTIONS, this.settings.transition)],
      onChange: this.handleTransitionChange.bind(this)
    });

  }

  buildShowArrowsToggle() {

    this.showArrowsToggle = new ToggleInput({
      on: true,
      off: false,
      value: this.settings.showArrows,
      onChange: this.handleShowArrowsChange.bind(this)
    });

  }

  buildShowDotsToggle() {

    this.showDotsToggle = new ToggleInput({
      on: true,
      off: false,
      value: this.settings.showDots,
      onChange: this.handleShowDotsChange.bind(this)
    });

  }

  buildAutoplayToggle() {

    this.autoplayToggle = new ToggleInput({
      on: true,
      off: false,
      value: this.settings.autoplay,
      onChange: this.handleAutoplayChange.bind(this)
    });

  }

  buildAutoplayIntervalInput() {

    this.autoplayIntervalInput = new CSSNumberValueControl({
      value: this.settings.autoplayInterval,
      onChange: this.handleAutoplayIntervalInput.bind(this)
    });

  }

  buildLoopToggle() {

    this.loopToggle = new ToggleInput({
      on: true,
      off: false,
      value: this.settings.loop,
      onChange: this.handleLoopChange.bind(this)
    });

  }

  buildFullViewToggle() {

    this.fullViewToggle = new ToggleInput({
      on: true,
      off: false,
      value: this.settings.fullViewEnabled,
      onChange: this.handleFullViewChange.bind(this)
    });

  }

  buildCaptionToggle() {

    this.captionToggle = new ToggleInput({
      on: true,
      off: false,
      value: this.settings.captionEnabled,
      onChange: this.handleCaptionToggleChange.bind(this)
    });

  }

  buildCaptionPositionSelect() {

    this.captionPositionSelect = new SingleSelectInputBox({
      placeholder: 'Select position',
      options: POSITION_OPTIONS,
      selectedOptionIndices: [getOptionIndex(POSITION_OPTIONS, this.settings.captionPosition)],
      onChange: this.handleCaptionPositionChange.bind(this)
    });

  }

  buildCaptionAppearanceSelect() {

    this.captionAppearanceSelect = new SingleSelectInputBox({
      placeholder: 'Select appearance',
      options: APPEARANCE_OPTIONS,
      selectedOptionIndices: [getOptionIndex(APPEARANCE_OPTIONS, this.settings.captionAppearance)],
      onChange: this.handleCaptionAppearanceChange.bind(this)
    });

  }

  handleItemSpacingInput(value) {

    this.handleItemSpacingChange(parseInt(value, 10));

  }

  handleItemSpacingChange(value) {

    if (isNaN(value) === true) {
      return;
    }

    if (value < 0) {
      return;
    }

    this.settings.itemSpacing = value;
    this.propagateChange();

  }

  handleTransitionChange(selection) {

    this.settings.transition = selection.values[0];
    this.propagateChange();

  }

  handleShowArrowsChange(value) {

    this.settings.showArrows = value;
    this.propagateChange();

  }

  handleShowDotsChange(value) {

    this.settings.showDots = value;
    this.propagateChange();

  }

  handleAutoplayChange(value) {

    this.settings.autoplay = value;

    this.applyAutoplayVisibility();
    this.propagateChange();

  }

  handleAutoplayIntervalInput(value) {

    this.handleAutoplayIntervalChange(parseInt(value, 10));

  }

  handleAutoplayIntervalChange(value) {

    if (isNaN(value) === true) {
      return;
    }

    if (value < 1) {
      return;
    }

    this.settings.autoplayInterval = value;
    this.propagateChange();

  }

  handleLoopChange(value) {

    this.settings.loop = value;
    this.propagateChange();

  }

  handleFullViewChange(value) {

    this.settings.fullViewEnabled = value;
    this.propagateChange();

  }

  handleCaptionToggleChange(value) {

    this.settings.captionEnabled = value;

    this.applyCaptionVisibility();
    this.propagateChange();

  }

  handleCaptionPositionChange(selection) {

    this.settings.captionPosition = selection.values[0];
    this.propagateChange();

  }

  handleCaptionAppearanceChange(selection) {

    this.settings.captionAppearance = selection.values[0];
    this.propagateChange();

  }

  applyAutoplayVisibility() {

    if (this.settings.autoplay === true) {
      setStyle(this.autoplaySubSettings, { display: '' });
      return;
    }

    setStyle(this.autoplaySubSettings, { display: 'none' });

  }

  applyCaptionVisibility() {

    if (this.settings.captionEnabled === true) {
      setStyle(this.captionSubSettings, { display: '' });
      return;
    }

    setStyle(this.captionSubSettings, { display: 'none' });

  }

  propagateChange() {

    this.onChange({ ...this.settings });

  }

}

export default CarouselSettings;